import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Chip,
  Card,
  CardContent,
  Divider,
  TextField,
  Grid,
  IconButton,
  CircularProgress,
  Autocomplete,
  Tooltip,
  Paper,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ShoppingCart from '@mui/icons-material/ShoppingCart';
import Add from '@mui/icons-material/Add';
import Remove from '@mui/icons-material/Remove';
import Check from '@mui/icons-material/Check';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Search from '@mui/icons-material/Search';
import FilterList from '@mui/icons-material/FilterList';
import Receipt from '@mui/icons-material/Receipt';
import ConfirmationNumber from '@mui/icons-material/ConfirmationNumber';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useData } from '../../contexts/DataContext';
import { Fee, PricingTier } from '../../types';
import { QuestionnaireDialog } from '../questionnaire/QuestionnaireDialog';
import { SignatureDialog } from '../signature/SignatureDialog';

const ITEMS_PER_PAGE = 12;

export const FeeCatalog: React.FC = () => {
  const { student, user } = useAuth();
  const { addToCart, cart, hasActivityPass, getActivityPassQuantityRemaining } = useCart();
  const { fees: mockFees, schoolYears: mockSchoolYears, feeCategories: mockFeeCategories } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSchoolYears, setSelectedSchoolYears] = useState<string[]>([]);
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [quantities, setQuantities] = useState<Map<string, number>>(new Map());
  const [loadingItems, setLoadingItems] = useState<Set<string>>(new Set());
  const [displayCount, setDisplayCount] = useState(ITEMS_PER_PAGE);
  const [paymentPercentages, setPaymentPercentages] = useState<Map<string, number>>(new Map());
  const [partialPaymentMode, setPartialPaymentMode] = useState<Map<string, boolean>>(new Map());
  const [customAmounts, setCustomAmounts] = useState<Map<string, number>>(new Map());
  const [selectedPresetAmounts, setSelectedPresetAmounts] = useState<Map<string, number | 'custom'>>(new Map());
  const [questionnaireDialog, setQuestionnaireDialog] = useState<{
    open: boolean;
    fee?: Fee;
    quantity: number;
  }>({ open: false, quantity: 1 });
  const [signatureDialog, setSignatureDialog] = useState<{
    open: boolean;
    fee?: Fee;
    quantity: number;
  }>({ open: false, quantity: 1 });

  const observerRef = useRef<HTMLDivElement>(null);
  const pricingTier: PricingTier = student?.pricingTier || 'full';

  const availableSchoolYears = useMemo(() => {
    const today = new Date();
    return mockSchoolYears
      .filter(sy => sy.endDate >= today)
      .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  }, []);

  const filteredFees = useMemo(() => {
    const filtered = mockFees.filter(fee => {
      if (!fee.isActive) return false;

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          fee.name.toLowerCase().includes(query) ||
          fee.description.toLowerCase().includes(query) ||
          (fee.course && fee.course.toLowerCase().includes(query));
        if (!matchesSearch) return false;
      }

      if (selectedSchoolYears.length > 0 && !selectedSchoolYears.includes(fee.schoolYearIds[0])) return false;
      if (selectedCourses.length > 0 && (!fee.course || !selectedCourses.includes(fee.course))) return false;
      if (selectedCategories.length > 0 && !selectedCategories.includes(fee.categoryId)) return false;
      return true;
    });

    return filtered.sort((a, b) => {
      if (a.categoryId === '1' && b.categoryId !== '1') return -1;
      if (a.categoryId !== '1' && b.categoryId === '1') return 1;
      return 0;
    });
  }, [searchQuery, selectedSchoolYears, selectedCourses, selectedCategories]);

  const displayedFees = useMemo(() => {
    return filteredFees.slice(0, displayCount);
  }, [filteredFees, displayCount]);

  const availableCourses = useMemo(() => {
    const courses = new Set<string>();
    mockFees.forEach(fee => {
      if (fee.course && fee.isActive) {
        courses.add(fee.course);
      }
    });
    return Array.from(courses).sort();
  }, []);

  const availableCategories = useMemo(() => {
    return mockFeeCategories
      .filter(cat => cat.isActive)
      .map(cat => ({ value: cat.id, label: cat.name }));
  }, []);

  const loadMore = useCallback(() => {
    if (displayCount < filteredFees.length) {
      setDisplayCount(prev => Math.min(prev + ITEMS_PER_PAGE, filteredFees.length));
    }
  }, [displayCount, filteredFees.length]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [loadMore]);

  useEffect(() => {
    setDisplayCount(ITEMS_PER_PAGE);
  }, [searchQuery, selectedSchoolYears, selectedCourses, selectedCategories]);

  // Tour-driven search demo. The guided tour can dispatch one of a
  // few search triggers; each types a different query letter-by-letter
  // so the booth audience watches the catalog filter live. Any other
  // trigger (or step move-on) clears the field.
  //   - fill-catalog-search           → "Graphing Calculator" (step 5)
  //   - search-catalog-activity-pass  → "Activity Pass" (step 7 — used
  //     instead of scrolling deep into the catalog to find the card)
  useEffect(() => {
    let typingTimer: ReturnType<typeof setTimeout> | null = null;
    let tourFilled = false;
    // Track which step "owns" the current search so we can clear it
    // when the step changes — but NOT when other in-step triggers
    // fire (e.g. step 9's 'bus-pass-partial-mode-on' fires within the
    // same step and shouldn't reset the search).
    let ownerStepId: string | null = null;
    // Different triggers want different lead-in delays. Step 5's
    // 'fill-catalog-search' fires AFTER a cursor click on the search
    // box — a tight 400ms feels like "user clicked, paused briefly,
    // then started typing." Steps 7/9 fire at step entry with no
    // prior interaction — a longer 1500ms gives the booth audience
    // time to register the catalog landing before the search animates.
    const planFor = (trigger?: string): { query: string; initialDelayMs: number } | null => {
      if (trigger === 'fill-catalog-search') return { query: 'Graphing Calculator', initialDelayMs: 400 };
      if (trigger === 'search-catalog-activity-pass') return { query: 'Activity Pass', initialDelayMs: 1500 };
      if (trigger === 'search-catalog-bus-pass') return { query: 'Semester Bus Pass', initialDelayMs: 1500 };
      if (trigger === 'search-catalog-museum-trip') return { query: 'Science Museum Trip', initialDelayMs: 1500 };
      return null;
    };
    const onTrigger = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const stepId: string | undefined = detail?.stepId;
      const plan = planFor(detail?.trigger);
      if (plan !== null) {
        // Make sure the search box is in view before typing starts —
        // if the page is scrolled (e.g. from a previous step's
        // anchor) the booth audience misses the typing animation.
        window.scrollTo({ top: 0, behavior: 'auto' });
        tourFilled = true;
        ownerStepId = stepId ?? null;
        setSearchQuery('');
        let i = 0;
        const typeNext = () => {
          i += 1;
          setSearchQuery(plan.query.slice(0, i));
          // After each char, dispatch the tour's scoped refresh event
          // so useTargetRect re-measures the spotlight target — search
          // filtering can move a card upward (other cards unmount
          // above it) without firing a scroll event, which would
          // leave the spotlight on a stale rect. Custom event keeps
          // this tour-only — a real 'resize' would wake every MUI
          // responsive component on the page (visible jitter).
          requestAnimationFrame(() => window.dispatchEvent(new Event('cc-tour-rect-refresh')));
          if (i < plan.query.length) {
            typingTimer = setTimeout(typeNext, 90);
          }
        };
        typingTimer = setTimeout(typeNext, plan.initialDelayMs);
      } else if (tourFilled && stepId !== ownerStepId) {
        // Step actually changed — clear the search. Within-step
        // triggers (e.g. cursor click follow-ups) hit this branch but
        // their stepId matches; we ignore those.
        tourFilled = false;
        ownerStepId = null;
        if (typingTimer) clearTimeout(typingTimer);
        setSearchQuery('');
      }
    };
    window.addEventListener('cc-tour-trigger', onTrigger);
    return () => {
      window.removeEventListener('cc-tour-trigger', onTrigger);
      if (typingTimer) clearTimeout(typingTimer);
    };
  }, []);

  // Tour-driven activity pass add. When the cursor "clicks" the
  // Add to Cart button on the activity pass card, the trigger fires
  // here and we add the pass programmatically (the cursor click is
  // visual-only — no real DOM click event). We bypass handleAddToCart
  // to skip questionnaire/signature dialogs and use raw addToCart.
  // We also handle `expand-catalog` (step entry) which bumps the
  // pagination so the activity pass is in DOM and findable by the
  // spotlight (default page size = 12, activity pass sits later in
  // the list).
  // Step 13 hits this with `cart-add-museum-trip` — but for that
  // step we WANT the signature dialog to appear, so we open it the
  // same way the real Add-to-Cart click would.
  useEffect(() => {
    const onTrigger = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.trigger === 'expand-catalog') {
        setDisplayCount(mockFees.length);
        return;
      }
      if (detail?.trigger === 'cart-add-museum-trip') {
        const museum = mockFees.find((f) => f.name === 'Science Museum Trip' && f.isActive);
        if (!museum) return;
        if (cart.items.some((it) => it.fee.id === museum.id)) return;
        // Open the signature dialog directly — same as the real
        // handleAddToCart path for fees with requiresSignature.
        setSignatureDialog({ open: true, fee: museum, quantity: 1 });
        return;
      }
      if (detail?.trigger !== 'cart-add-activity-pass') return;
      const pass = mockFees.find((f) => f.isActivityPass && f.isActive);
      if (!pass) return;
      // Don't double-add if the user replays the step.
      if (cart.items.some((it) => it.fee.id === pass.id)) return;
      addToCart(pass, 1, pricingTier);
    };
    window.addEventListener('cc-tour-trigger', onTrigger);
    return () => window.removeEventListener('cc-tour-trigger', onTrigger);
  }, [addToCart, cart.items, pricingTier]);

  // Tour-driven partial-payment demo (step 9). The tour cursor "clicks"
  // the Partial Payment toggle and the 50% percentage button on the
  // Semester Bus Pass card; these triggers do the actual state mutation
  // since the cursor click is visual-only. When step 9 ends (any other
  // trigger fires), reset the bus pass back to full / 100%.
  const BUS_PASS_FEE_ID = '850e8400-e29b-41d4-a716-446655440002';
  useEffect(() => {
    let tourTouched = false;
    let ownerStepId: string | null = null;
    const onTrigger = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const stepId: string | undefined = detail?.stepId;
      if (detail?.trigger === 'bus-pass-partial-mode-on') {
        tourTouched = true;
        ownerStepId = stepId ?? null;
        setPartialPaymentMode(prev => new Map(prev).set(BUS_PASS_FEE_ID, true));
        setPaymentPercentages(prev => new Map(prev).set(BUS_PASS_FEE_ID, 100));
      } else if (detail?.trigger === 'bus-pass-partial-50') {
        tourTouched = true;
        ownerStepId = stepId ?? null;
        setPaymentPercentages(prev => new Map(prev).set(BUS_PASS_FEE_ID, 50));
      } else if (tourTouched && stepId !== ownerStepId) {
        // Step changed — restore defaults so the bus pass doesn't
        // stay in partial mode for unrelated steps.
        tourTouched = false;
        ownerStepId = null;
        setPartialPaymentMode(prev => {
          const next = new Map(prev);
          next.delete(BUS_PASS_FEE_ID);
          return next;
        });
        setPaymentPercentages(prev => {
          const next = new Map(prev);
          next.delete(BUS_PASS_FEE_ID);
          return next;
        });
      }
    };
    window.addEventListener('cc-tour-trigger', onTrigger);
    return () => window.removeEventListener('cc-tour-trigger', onTrigger);
  }, []);

  useEffect(() => {
    mockFees.forEach(fee => {
      if (fee.allowsCustomAmount && fee.customAmountPresets && fee.customAmountPresets.length > 0) {
        if (!selectedPresetAmounts.has(fee.id)) {
          const defaultAmount = fee.customAmountPresets[0];
          setSelectedPresetAmounts(prev => new Map(prev).set(fee.id, defaultAmount));
          setCustomAmounts(prev => new Map(prev).set(fee.id, defaultAmount));
        }
      }
    });
  }, []);

  const getCategoryLabel = (categoryId: string) => {
    const category = mockFeeCategories.find(c => c.id === categoryId);
    return category?.name || 'Other';
  };

  const getSchoolYearName = (schoolYearId: string) => {
    const sy = mockSchoolYears.find(s => s.id === schoolYearId);
    return sy?.name || '';
  };

  const getBasePrice = (fee: Fee): number => {
    if (fee.allowsCustomAmount) {
      const selectedAmount = selectedPresetAmounts.get(fee.id);
      if (selectedAmount === 'custom') {
        return customAmounts.get(fee.id) || fee.basePrice;
      } else if (typeof selectedAmount === 'number') {
        return selectedAmount;
      }
      return fee.basePrice;
    }

    switch (pricingTier) {
      case 'full':
        return fee.basePrice;
      case 'reduced':
        return fee.reducedPrice;
      case 'free':
        return fee.freePrice;
      default:
        return fee.basePrice;
    }
  };

  const getActivityPassPrice = (fee: Fee): number | null => {
    if (!hasActivityPass || !fee.activityPassPricing?.enabled) return null;
    const remaining = getActivityPassQuantityRemaining(fee.id);
    if (remaining <= 0) return null;
    return fee.activityPassPricing.isFree ? 0 : fee.activityPassPricing.price;
  };

  const getPrice = (fee: Fee, quantity: number = 1): { unitPrice: number; activityPassQty: number; regularQty: number; totalPrice: number; activityPassDiscount: number } => {
    const basePrice = getBasePrice(fee);

    if (!hasActivityPass || !fee.activityPassPricing?.enabled) {
      return {
        unitPrice: basePrice,
        activityPassQty: 0,
        regularQty: quantity,
        totalPrice: basePrice * quantity,
        activityPassDiscount: 0,
      };
    }

    const activityPassPrice = fee.activityPassPricing.isFree ? 0 : fee.activityPassPricing.price;
    const remaining = getActivityPassQuantityRemaining(fee.id);
    const activityPassQty = Math.min(remaining, quantity);
    const regularQty = quantity - activityPassQty;
    const activityPassTotal = activityPassQty * activityPassPrice;
    const regularTotal = regularQty * basePrice;
    const totalPrice = activityPassTotal + regularTotal;
    const fullPrice = basePrice * quantity;
    const activityPassDiscount = fullPrice - totalPrice;

    return {
      unitPrice: activityPassQty > 0 ? activityPassPrice : basePrice,
      activityPassQty,
      regularQty,
      totalPrice,
      activityPassDiscount,
    };
  };

  const handleCustomAmountChange = (feeId: string, amount: number) => {
    setCustomAmounts(prev => new Map(prev).set(feeId, amount));
  };

  const handlePresetAmountSelect = (feeId: string, amount: number | 'custom') => {
    setSelectedPresetAmounts(prev => new Map(prev).set(feeId, amount));
    if (amount !== 'custom' && typeof amount === 'number') {
      setCustomAmounts(prev => new Map(prev).set(feeId, amount));
    }
  };

  const isInCart = (feeId: string, tier: PricingTier) => {
    return cart.items.some(item => item.fee.id === feeId && item.pricingTier === tier);
  };

  const handleQuantityChange = (feeId: string, newQuantity: number, maxQuantity?: number) => {
    const clampedQuantity = Math.max(1, maxQuantity ? Math.min(newQuantity, maxQuantity) : newQuantity);
    setQuantities(prev => new Map(prev).set(feeId, clampedQuantity));
  };

  const handlePaymentPercentageChange = (feeId: string, percentage: number) => {
    setPaymentPercentages(prev => new Map(prev).set(feeId, percentage));
  };

  const handlePartialPaymentModeToggle = (feeId: string, isPartial: boolean) => {
    setPartialPaymentMode(prev => new Map(prev).set(feeId, isPartial));
    if (!isPartial) {
      setPaymentPercentages(prev => new Map(prev).set(feeId, 100));
    }
  };

  const buildActivityPassInfo = (fee: Fee, quantity: number) => {
    const priceInfo = getPrice(fee, quantity);
    if (!hasActivityPass || !fee.activityPassPricing?.enabled || priceInfo.activityPassQty === 0) {
      return undefined;
    }
    const basePrice = getBasePrice(fee);
    const activityPassPrice = fee.activityPassPricing.isFree ? 0 : fee.activityPassPricing.price;
    return {
      activityPassQty: priceInfo.activityPassQty,
      regularQty: priceInfo.regularQty,
      activityPassPrice,
      regularPrice: basePrice,
      totalPrice: priceInfo.totalPrice,
      activityPassDiscount: priceInfo.activityPassDiscount,
    };
  };

  const handleQuestionnaireSubmit = (answers: Record<string, string>) => {
    if (questionnaireDialog.fee) {
      const fee = questionnaireDialog.fee;
      const quantity = questionnaireDialog.quantity;
      setQuestionnaireDialog({ open: false, quantity: 1 });

      const priceInfo = getPrice(fee, quantity);
      const customUnitPrice = fee.allowsCustomAmount ? priceInfo.unitPrice : (priceInfo.activityPassQty > 0 ? priceInfo.unitPrice : undefined);
      const activityPassInfo = buildActivityPassInfo(fee, quantity);

      let partialPaymentAmount: number | undefined = undefined;
      let partialPaymentPercentage: number | undefined = undefined;
      if (fee.allowsPartialPayment && partialPaymentMode.get(fee.id)) {
        const percentage = paymentPercentages.get(fee.id) ?? 100;
        partialPaymentPercentage = percentage;
        partialPaymentAmount = (priceInfo.totalPrice * percentage) / 100;
      }

      if (fee.termsAndConditions?.requiresSignature) {
        setSignatureDialog({ open: true, fee, quantity });
      } else {
        addToCart(fee, quantity, pricingTier, answers, undefined, partialPaymentAmount, customUnitPrice, partialPaymentPercentage, undefined, activityPassInfo);
        setLoadingItems(prev => {
          const next = new Set(prev);
          next.delete(fee.id);
          return next;
        });
      }
    }
  };

  const handleSignatureSubmit = (signature: string) => {
    if (signatureDialog.fee) {
      const fee = signatureDialog.fee;
      const quantity = signatureDialog.quantity;
      const answers = questionnaireDialog.fee?.questionnaire ? {} : undefined;

      const priceInfo = getPrice(fee, quantity);
      const customUnitPrice = fee.allowsCustomAmount ? priceInfo.unitPrice : (priceInfo.activityPassQty > 0 ? priceInfo.unitPrice : undefined);
      const activityPassInfo = buildActivityPassInfo(fee, quantity);

      let partialPaymentAmount: number | undefined = undefined;
      let partialPaymentPercentage: number | undefined = undefined;
      if (fee.allowsPartialPayment && partialPaymentMode.get(fee.id)) {
        const percentage = paymentPercentages.get(fee.id) ?? 100;
        partialPaymentPercentage = percentage;
        partialPaymentAmount = (priceInfo.totalPrice * percentage) / 100;
      }

      setSignatureDialog({ open: false, quantity: 1 });
      addToCart(fee, quantity, pricingTier, answers, signature, partialPaymentAmount, customUnitPrice, partialPaymentPercentage, undefined, activityPassInfo);
      setLoadingItems(prev => {
        const next = new Set(prev);
        next.delete(fee.id);
        return next;
      });
    }
  };

  const handleAddToCart = (fee: Fee) => {
    if (isInCart(fee.id, pricingTier)) return;

    setLoadingItems(prev => new Set(prev).add(fee.id));
    const quantity = quantities.get(fee.id) ?? 1;

    const priceInfo = getPrice(fee, quantity);
    const customUnitPrice = fee.allowsCustomAmount ? priceInfo.unitPrice : (priceInfo.activityPassQty > 0 ? priceInfo.unitPrice : undefined);
    const activityPassInfo = buildActivityPassInfo(fee, quantity);

    let partialPaymentAmount: number | undefined = undefined;
    let partialPaymentPercentage: number | undefined = undefined;
    if (fee.allowsPartialPayment && partialPaymentMode.get(fee.id)) {
      const percentage = paymentPercentages.get(fee.id) ?? 100;
      partialPaymentPercentage = percentage;
      partialPaymentAmount = (priceInfo.totalPrice * percentage) / 100;
    }

    if (fee.questionnaire && fee.questionnaire.length > 0) {
      setQuestionnaireDialog({ open: true, fee, quantity });
    } else if (fee.termsAndConditions?.requiresSignature) {
      setSignatureDialog({ open: true, fee, quantity });
    } else {
      addToCart(fee, quantity, pricingTier, undefined, undefined, partialPaymentAmount, customUnitPrice, partialPaymentPercentage, undefined, activityPassInfo);
      setLoadingItems(prev => {
        const next = new Set(prev);
        next.delete(fee.id);
        return next;
      });
    }
  };

  return (
    <>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 50%, #0891b2 100%)',
          color: 'white',
          py: { xs: 3, sm: 4, md: 5 },
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Receipt sx={{ fontSize: { xs: 40, md: 50 } }} />
            <Typography
              variant="h2"
              fontWeight={700}
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' }
              }}
            >
              Fee Catalog
            </Typography>
          </Box>
          <Typography
            variant="h6"
            sx={{
              opacity: 0.95,
              fontWeight: 400,
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }
            }}
          >
            Browse and add items to your cart
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 3, sm: 4, md: 6 }, px: { xs: 2, sm: 3 } }}>
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <FilterList />
            <Typography variant="h6" fontWeight={600}>
              Search & Filter Fees
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }} data-tour="catalog-search">
              <TextField
                fullWidth
                placeholder="Search fees, courses, or descriptions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Autocomplete
                multiple
                options={availableSchoolYears}
                getOptionLabel={(option) => option.name}
                value={availableSchoolYears.filter(sy => selectedSchoolYears.includes(sy.id))}
                onChange={(_, newValue) => {
                  setSelectedSchoolYears(newValue.map(sy => sy.id));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="School Year" placeholder="Filter" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.name}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Autocomplete
                multiple
                options={availableCourses}
                value={selectedCourses}
                onChange={(_, newValue) => {
                  setSelectedCourses(newValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Course" placeholder="Filter" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Autocomplete
                multiple
                options={availableCategories}
                getOptionLabel={(option) => option.label}
                value={availableCategories.filter(cat => selectedCategories.includes(cat.value))}
                onChange={(_, newValue) => {
                  setSelectedCategories(newValue.map(cat => cat.value));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Category" placeholder="Filter" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.label}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))
                }
              />
            </Grid>
          </Grid>

          {(searchQuery || selectedSchoolYears.length > 0 || selectedCourses.length > 0 || selectedCategories.length > 0) && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Active filters:
              </Typography>
              {searchQuery && (
                <Chip
                  label={`Search: "${searchQuery}"`}
                  size="small"
                  onDelete={() => setSearchQuery('')}
                />
              )}
              {selectedSchoolYears.map(syId => {
                const sy = availableSchoolYears.find(s => s.id === syId);
                return sy ? (
                  <Chip
                    key={syId}
                    label={`Year: ${sy.name}`}
                    size="small"
                    onDelete={() => setSelectedSchoolYears(prev => prev.filter(id => id !== syId))}
                  />
                ) : null;
              })}
              {selectedCourses.map(course => (
                <Chip
                  key={course}
                  label={`Course: ${course}`}
                  size="small"
                  onDelete={() => setSelectedCourses(prev => prev.filter(c => c !== course))}
                />
              ))}
              {selectedCategories.map(cat => {
                const category = availableCategories.find(c => c.value === cat);
                return category ? (
                  <Chip
                    key={cat}
                    label={`Category: ${category.label}`}
                    size="small"
                    onDelete={() => setSelectedCategories(prev => prev.filter(c => c !== cat))}
                  />
                ) : null;
              })}
            </Box>
          )}
        </Paper>

        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {filteredFees.length} {filteredFees.length === 1 ? 'fee' : 'fees'} found
          </Typography>
          {(searchQuery || selectedSchoolYears.length > 0 || selectedCourses.length > 0 || selectedCategories.length > 0) && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSearchQuery('');
                setSelectedSchoolYears([]);
                setSelectedCourses([]);
                setSelectedCategories([]);
              }}
            >
              Clear All Filters
            </Button>
          )}
        </Box>

        {displayedFees.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              bgcolor: 'background.default',
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Fees Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {searchQuery || selectedSchoolYears.length > 0 || selectedCourses.length > 0 || selectedCategories.length > 0
                ? 'Try adjusting your filters or search terms to find more fees.'
                : 'No fees are currently available.'}
            </Typography>
            {(searchQuery || selectedSchoolYears.length > 0 || selectedCourses.length > 0 || selectedCategories.length > 0) && (
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedSchoolYears([]);
                  setSelectedCourses([]);
                  setSelectedCategories([]);
                }}
              >
                Clear All Filters
              </Button>
            )}
          </Box>
        ) : (
          <>
            <Grid container spacing={2} data-tour="catalog-grid">
              {displayedFees.map(fee => {
                const currentQuantity = quantities.get(fee.id) ?? 1;
                const priceInfo = getPrice(fee, currentQuantity);
                const activityPassPrice = getActivityPassPrice(fee);
                const basePrice = getBasePrice(fee);
                const isPartialMode = partialPaymentMode.get(fee.id) || false;
                const selectedPercentage = paymentPercentages.get(fee.id) ?? 100;
                const paymentAmount = isPartialMode ? (priceInfo.totalPrice * selectedPercentage) / 100 : priceInfo.totalPrice;
                const minPayment = priceInfo.totalPrice * 0.1;
                const activityPassRemaining = getActivityPassQuantityRemaining(fee.id);

                return (
                  <Grid
                    size={{ xs: 12, md: 6 }}
                    key={fee.id}
                    data-tour-fee-id={fee.id}
                    {...(fee.isActivityPass
                      ? { 'data-tour': 'catalog-activity-pass-card' }
                      : fee.id === '850e8400-e29b-41d4-a716-446655440002'
                        ? { 'data-tour': 'catalog-bus-pass-card' }
                        : fee.name === 'Science Museum Trip'
                          ? { 'data-tour': 'catalog-museum-trip-card' }
                          : {})}
                  >
                    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography variant="h6" fontWeight={600} sx={{ flex: 1, pr: 2 }}>
                            {fee.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {fee.isActivityPass && (
                              <Chip
                                data-tour="catalog-activity-pass"
                                icon={<ConfirmationNumber sx={{ fontSize: 16 }} />}
                                label="Activity Pass"
                                size="small"
                                sx={{
                                  bgcolor: '#fef3c7',
                                  color: '#92400e',
                                  fontWeight: 600,
                                  border: '1px solid #fbbf24',
                                }}
                              />
                            )}
                            {fee.allowsPartialPayment && (
                              <Chip
                                label="Partial Payment"
                                size="small"
                                sx={{
                                  bgcolor: '#fef3c7',
                                  color: '#92400e',
                                  fontWeight: 600,
                                  border: '1px solid #fbbf24',
                                }}
                              />
                            )}
                            <Chip
                              label={getSchoolYearName(fee.schoolYearIds[0])}
                              size="small"
                              sx={{
                                bgcolor: '#e3f2fd',
                                color: '#1565c0',
                                fontWeight: 600,
                                border: '1px solid #90caf9',
                              }}
                            />
                            <Chip
                              label={getCategoryLabel(fee.categoryId)}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            {fee.course && (
                              <Chip
                                label={fee.course}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {fee.description}
                        </Typography>

                        {fee.activityPassPricing?.enabled && (
                          <Box sx={{ mb: 2, p: 1.5, bgcolor: hasActivityPass ? '#f0fdf4' : '#f5f5f5', borderRadius: 1, border: '1px solid', borderColor: hasActivityPass ? '#bbf7d0' : '#e0e0e0' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <ConfirmationNumber sx={{ fontSize: 18, color: hasActivityPass ? 'success.main' : 'text.secondary' }} />
                              <Typography variant="body2" fontWeight={600} color={hasActivityPass ? 'success.dark' : 'text.secondary'}>
                                Activity Pass Pricing
                              </Typography>
                            </Box>
                            <Typography variant="body2">
                              {fee.activityPassPricing.isFree ? (
                                <strong style={{ color: hasActivityPass ? '#16a34a' : '#666' }}>FREE</strong>
                              ) : (
                                <strong style={{ color: hasActivityPass ? '#16a34a' : '#666' }}>${fee.activityPassPricing.price.toFixed(2)}</strong>
                              )}
                              {' '} for up to {fee.activityPassPricing.quantity} ticket{fee.activityPassPricing.quantity > 1 ? 's' : ''}
                            </Typography>
                            {hasActivityPass && activityPassRemaining > 0 && (
                              <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                                You have {activityPassRemaining} remaining at this price
                              </Typography>
                            )}
                            {hasActivityPass && activityPassRemaining <= 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Activity pass quantity used - regular pricing applies
                              </Typography>
                            )}
                            {!hasActivityPass && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Purchase a Student Activity Pass to unlock this price
                              </Typography>
                            )}
                          </Box>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <Grid container spacing={2}>
                          {fee.allowsCustomAmount ? (
                            <>
                              <Grid size={{ xs: 12 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  Select Donation Amount
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                  {fee.customAmountPresets?.map(amount => (
                                    <Button
                                      key={amount}
                                      size="medium"
                                      variant={selectedPresetAmounts.get(fee.id) === amount ? 'contained' : 'outlined'}
                                      onClick={() => handlePresetAmountSelect(fee.id, amount)}
                                      sx={{
                                        minWidth: '80px',
                                        flex: '1 1 auto',
                                      }}
                                    >
                                      ${amount}
                                    </Button>
                                  ))}
                                  <Button
                                    size="medium"
                                    variant={selectedPresetAmounts.get(fee.id) === 'custom' ? 'contained' : 'outlined'}
                                    onClick={() => handlePresetAmountSelect(fee.id, 'custom')}
                                    sx={{
                                      minWidth: '80px',
                                      flex: '1 1 auto',
                                    }}
                                  >
                                    Custom
                                  </Button>
                                </Box>
                                {selectedPresetAmounts.get(fee.id) === 'custom' && (
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Custom Amount"
                                    type="number"
                                    value={customAmounts.get(fee.id) || ''}
                                    onChange={(e) => {
                                      const val = parseFloat(e.target.value);
                                      if (!isNaN(val) && val > 0) {
                                        handleCustomAmountChange(fee.id, val);
                                      }
                                    }}
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    }}
                                    placeholder="Enter amount"
                                    sx={{ mb: 1 }}
                                  />
                                )}
                              </Grid>
                            </>
                          ) : (
                            <Grid size={{ xs: 12 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Quantity
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleQuantityChange(fee.id, currentQuantity - 1, fee.maxQuantity)}
                                  disabled={!fee.requiresQuantity || currentQuantity <= 1}
                                  sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    '&:hover': { bgcolor: 'action.hover' }
                                  }}
                                >
                                  <Remove fontSize="small" />
                                </IconButton>
                                <TextField
                                  size="small"
                                  value={currentQuantity}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (!isNaN(val) && fee.requiresQuantity) {
                                      handleQuantityChange(fee.id, val, fee.maxQuantity);
                                    }
                                  }}
                                  disabled={!fee.requiresQuantity}
                                  inputProps={{
                                    style: { textAlign: 'center', padding: '8px 12px' },
                                    min: 1,
                                    max: fee.maxQuantity ?? 99,
                                  }}
                                  sx={{ width: '70px' }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => handleQuantityChange(fee.id, currentQuantity + 1, fee.maxQuantity)}
                                  disabled={!fee.requiresQuantity || (fee.maxQuantity ? currentQuantity >= fee.maxQuantity : false)}
                                  sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    '&:hover': { bgcolor: 'action.hover' }
                                  }}
                                >
                                  <Add fontSize="small" />
                                </IconButton>
                                {fee.maxQuantity && (
                                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    Max: {fee.maxQuantity}
                                  </Typography>
                                )}
                              </Box>
                            </Grid>
                          )}

                          <Grid size={{ xs: 6 }} data-tour="catalog-price-tier">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                {fee.allowsCustomAmount ? 'Donation Amount' : 'Your Price'}
                              </Typography>
                              {!fee.allowsCustomAmount && (
                                <Tooltip
                                  title={priceInfo.activityPassDiscount > 0 ? 'Includes Activity Pass discount' : 'This price reflects any applicable pricing discounts'}
                                  arrow
                                  placement="top"
                                >
                                  <InfoOutlined sx={{ fontSize: 16, color: priceInfo.activityPassDiscount > 0 ? 'success.main' : 'text.secondary', cursor: 'help' }} />
                                </Tooltip>
                              )}
                            </Box>
                            {priceInfo.activityPassDiscount > 0 ? (
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                  ${(basePrice * currentQuantity).toFixed(2)}
                                </Typography>
                                <Typography variant="body1" fontWeight={600} color="success.main">
                                  ${priceInfo.totalPrice.toFixed(2)}
                                </Typography>
                                <Typography variant="caption" color="success.main">
                                  Save ${priceInfo.activityPassDiscount.toFixed(2)}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body1" fontWeight={500}>
                                ${priceInfo.totalPrice.toFixed(2)}
                              </Typography>
                            )}
                          </Grid>

                          {fee.allowsPartialPayment && (
                            <Grid size={{ xs: 12 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Payment Options
                              </Typography>
                              <ToggleButtonGroup
                                value={isPartialMode ? 'partial' : 'full'}
                                exclusive
                                onChange={(_, value) => {
                                  if (value !== null) {
                                    handlePartialPaymentModeToggle(fee.id, value === 'partial');
                                  }
                                }}
                                fullWidth
                                size="small"
                              >
                                <ToggleButton value="full">Pay in Full</ToggleButton>
                                <ToggleButton
                                  value="partial"
                                  {...(fee.id === '850e8400-e29b-41d4-a716-446655440002'
                                    ? { 'data-tour': 'catalog-partial-toggle' }
                                    : {})}
                                >
                                  Partial Payment
                                </ToggleButton>
                              </ToggleButtonGroup>
                            </Grid>
                          )}

                          {fee.allowsPartialPayment && isPartialMode && (
                            <Grid size={{ xs: 12 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Select Percentage
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                {[10, 25, 50, 75, 100].map(percentage => (
                                  <Button
                                    key={percentage}
                                    size="small"
                                    variant={selectedPercentage === percentage ? 'contained' : 'outlined'}
                                    onClick={() => handlePaymentPercentageChange(fee.id, percentage)}
                                    {...(fee.id === '850e8400-e29b-41d4-a716-446655440002' && percentage === 50
                                      ? { 'data-tour': 'catalog-partial-50' }
                                      : {})}
                                    sx={{
                                      minWidth: '48px',
                                      flex: '1 1 auto',
                                    }}
                                  >
                                    {percentage}%
                                  </Button>
                                ))}
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Min: ${minPayment.toFixed(2)}
                              </Typography>
                            </Grid>
                          )}

                          <Grid size={{ xs: 6 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                Total
                              </Typography>
                            </Box>
                            <Typography variant="h6" fontWeight={600} color="primary">
                              ${paymentAmount.toFixed(2)}
                            </Typography>
                          </Grid>
                        </Grid>

                        {/* Spacer pushes the action button to the bottom so
                            cards in the same row share the same button baseline. */}
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                          {...(fee.isActivityPass
                            ? { 'data-tour': 'catalog-activity-pass-add' }
                            : fee.name === 'Science Museum Trip'
                              ? { 'data-tour': 'catalog-museum-trip-add' }
                              : {})}
                          variant="contained"
                          fullWidth
                          startIcon={
                            loadingItems.has(fee.id) ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : isInCart(fee.id, pricingTier) ? (
                              <Check />
                            ) : (
                              <ShoppingCart />
                            )
                          }
                          onClick={() => handleAddToCart(fee)}
                          disabled={
                            loadingItems.has(fee.id) ||
                            isInCart(fee.id, pricingTier) ||
                            (fee.allowsCustomAmount && selectedPresetAmounts.get(fee.id) === 'custom' && (!customAmounts.get(fee.id) || customAmounts.get(fee.id)! <= 0))
                          }
                          sx={{ mt: 2, flexShrink: 0 }}
                        >
                          {loadingItems.has(fee.id)
                            ? 'Adding...'
                            : isInCart(fee.id, pricingTier)
                            ? 'In Cart'
                            : fee.allowsCustomAmount
                            ? 'Add Donation'
                            : 'Add to Cart'}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {displayCount < filteredFees.length && (
              <Box
                ref={observerRef}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 4,
                }}
              >
                <CircularProgress />
              </Box>
            )}

            {displayCount >= filteredFees.length && filteredFees.length > ITEMS_PER_PAGE && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing all {filteredFees.length} fees
                </Typography>
              </Box>
            )}
          </>
        )}
      </Container>

      <QuestionnaireDialog
        open={questionnaireDialog.open}
        onClose={() => {
          setQuestionnaireDialog({ open: false, quantity: 1 });
          if (questionnaireDialog.fee) {
            setLoadingItems(prev => {
              const next = new Set(prev);
              next.delete(questionnaireDialog.fee!.id);
              return next;
            });
          }
        }}
        onSubmit={handleQuestionnaireSubmit}
        questions={questionnaireDialog.fee?.questionnaire || []}
        title={`${questionnaireDialog.fee?.name} - Additional Information`}
      />

      <SignatureDialog
        open={signatureDialog.open && !!signatureDialog.fee?.termsAndConditions}
        onClose={() => {
          setSignatureDialog({ open: false, quantity: 1 });
          if (signatureDialog.fee) {
            setLoadingItems(prev => {
              const next = new Set(prev);
              next.delete(signatureDialog.fee!.id);
              return next;
            });
          }
        }}
        onSubmit={handleSignatureSubmit}
        terms={signatureDialog.fee?.termsAndConditions || { title: '', content: '', requiresSignature: false }}
        feeName={signatureDialog.fee?.name || ''}
        course={signatureDialog.fee?.course}
        signerName={user ? `${user.firstName} ${user.lastName}` : ''}
      />
    </>
  );
};
